import React from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';

import { Row, Col, Divider, Tooltip } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';

import { BUSINESS_UNIT_TYPES } from '../../../../../../utils/constants/business-unit-types';
import { OrderProduct } from '../../../../../../api-client';
import { IShoppingFacilityGroup } from '../../../../../models/Shopping';
import { initWideFormOrderLine, initWideFormProduct, toggleWideFormDrawer } from '../../../../../../slices/WideForm/wideFormSlice';
import { orderTotalByProducts, orderQuantityBySizes, orderTotalBySizes } from '../../../../../../utils/data-format-enchanters/order-products-enchanter';
import { formatCurrency } from '../../../../../../utils/data-format-enchanters/currency-enchanter';
import { keepProduct } from '../../../../../../slices/OrderSummary/orderSummarySlice';
import { rowLimitedEditionMismatch, rowLimitedEditionNumbers, trimSelectionToQuantity } from '../../../../../../utils/limited-edition-utils';
import { quotaPendingInOtherDraftsLabel, quotaViolationLabel, sapCodeOfSize, violationForSapCode } from '../../../../../../utils/quota-utils';

import './bv-summary-facility-group.scss';
import BVInputNumber from '../../../../BVControls/BVInputNumber/bv-input-number';

const BVSummaryFacilityGroup: React.FunctionComponent<IShoppingFacilityGroup> = ({ address, city, disabled, name, products, quotaViolations }) => {
    const dispatch = useDispatch();

    const _handleFacilityGroupClick = (orderProduct: OrderProduct) => {
        if (disabled) {
            return;
        }

        if (orderProduct.id && orderProduct.rowId) {
            dispatch(initWideFormOrderLine(orderProduct.rowId));
            dispatch(initWideFormProduct(orderProduct.id));
            dispatch(toggleWideFormDrawer(true));
        }
    }

    const _handleSizeChange = (dataItem: OrderProduct, value: number) => {
        // Lowering the quantity from here has to give the numbers in excess back, exactly as it does in the
        // product form: otherwise the row would keep holding pieces that are no longer ordered.
        let editedSizes = _.map(dataItem.sizes, entryItem => (
            entryItem.sapCode === dataItem.sizes![0].sapCode
                ? {
                    ...entryItem,
                    quantity: value,
                    limitedEditionCodes: trimSelectionToQuantity(entryItem.limitedEditionCodes, value)
                }
                : entryItem
        ));

        dispatch(keepProduct({ ...dataItem, sizes: editedSizes }));
    };

    return (
        <div className="bv-summary-facility-group-container">
            <Row>
                <Col span={12}>
                    <span className="heading">
                        {name}
                    </span>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                    <span className="heading">
                        {orderTotalByProducts(products).amountFormat}
                    </span>
                </Col>
                <Col span={24}>
                    <span className="secondary-heading">
                        {`${address}, ${city}`}
                    </span>
                </Col>
                <Col span={24}>
                    <table className="facility-group-table">
                        <tbody>
                            {_.map(products, (dataItem, dataIndex) => {
                                const violation = violationForSapCode(
                                    quotaViolations ?? [],
                                    sapCodeOfSize(dataItem.sizes && dataItem.sizes[0])
                                );

                                return (
                                <tr
                                    key={dataIndex}
                                    className={`facility-group-table-row ${disabled && 'facility-group-table-row-disabled'} ${violation ? 'facility-group-table-row-over-quota' : ''}`}
                                    onClick={() => _handleFacilityGroupClick(dataItem)}
                                >
                                    <td style={{ width: '15%' }}>
                                        <img src={dataItem.imageUrl} className="group-table-image" alt={dataItem.name} />
                                    </td>
                                    <td style={{ width: '35%' }}>
                                        <span className="group-table-text">
                                            {dataItem.name}
                                        </span>

                                        {rowLimitedEditionNumbers(dataItem).length > 0 &&
                                            <span className="group-table-limited-editions">
                                                {`L.E. ${rowLimitedEditionNumbers(dataItem).join(', ')}`}
                                            </span>
                                        }

                                        {rowLimitedEditionMismatch(dataItem) &&
                                            <span className="group-table-limited-editions-warning">
                                                {rowLimitedEditionMismatch(dataItem)}
                                            </span>
                                        }
                                    </td>
                                    <td style={{ width: '10%', textAlign: 'center' }}>
                                        <span className="group-table-text-small">
                                            {dataItem.businessUnitCode === BUSINESS_UNIT_TYPES.JEWELRY_CODE
                                                ? `CatCode`
                                                : `SapCode`
                                            }
                                        </span>
                                        <span className="group-table-text">
                                            {dataItem.businessUnitCode === BUSINESS_UNIT_TYPES.JEWELRY_CODE
                                                ? `${dataItem.catalogCode}`
                                                : `${dataItem.sizes && dataItem.sizes.length > 0 && dataItem.sizes[0].sapCode}`
                                            }
                                        </span>
                                        {violation &&
                                            <Tooltip title={quotaViolationLabel(violation)}>
                                                <span className="group-table-quota-warning">
                                                    {quotaViolationLabel(violation)}
                                                </span>
                                            </Tooltip>
                                        }
                                        {violation && quotaPendingInOtherDraftsLabel(violation) &&
                                            <span className="group-table-quota-pending">
                                                {quotaPendingInOtherDraftsLabel(violation)}
                                            </span>
                                        }
                                    </td>
                                    <td style={{ width: '10%', textAlign: 'center' }}>
                                        <span className="group-table-text" onClick={(e: any) => {
                                            if (dataItem.sizes?.length === 1) {
                                                e.stopPropagation();
                                                e.preventDefault();
                                            } else {
                                                return;
                                            }
                                        }}>
                                            {dataItem.sizes?.length === 1
                                                ?
                                                <BVInputNumber
                                                    className={dataItem.sizes && orderQuantityBySizes(dataItem.sizes) > 1 ? 'bv-size-control' : ''}
                                                    properties={{
                                                        value: orderQuantityBySizes(dataItem.sizes) || 0,
                                                        step: 1,
                                                        upHandler: <PlusOutlined />,
                                                        downHandler: <MinusOutlined />,
                                                        min: 1,
                                                        onChange: (value: number) => {
                                                            _handleSizeChange(dataItem, value);
                                                        },
                                                        onStep: (value: number, info: { offset: number, type: 'up' | 'down' }) => {
                                                            _handleSizeChange(dataItem, value);
                                                        },
                                                    }}
                                                />
                                                :
                                                `x ${orderQuantityBySizes(dataItem.sizes)}`
                                            }
                                        </span>
                                    </td>
                                    <td style={{ width: '15%', textAlign: 'center' }}>
                                        <span className="group-table-text-small">
                                            Unit Price
                                        </span>
                                        <span className="group-table-text">
                                            {dataItem.sizes && dataItem.sizes[0].priceNet &&
                                                `${formatCurrency(dataItem.sizes[0].priceNet.amount, dataItem.sizes[0].priceNet.currency)}`
                                            }
                                        </span>
                                    </td>
                                    <td style={{ width: '15%', textAlign: 'center' }}>
                                        <span className="group-table-text-small">
                                            Amount
                                        </span>
                                        <span className="group-table-text">
                                            {`${orderTotalBySizes(dataItem.sizes).amountFormat}`}
                                        </span>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Col>
            </Row>

            <Divider />
        </div>
    );
}

export default BVSummaryFacilityGroup;